import {
  GEOHASH_RESOLUTION,
  H3_RESOLUTION
} from "@/components/spatialAnalysis/spatialOutputAnalysis/data";
import { getErrorReasonMessage } from "@/locales/errorMessages";
import type { AdministrativeArea } from "@/types/adminArea";
import {
  DatasetFilterPayload,
  DatasetsMap,
  ProportionMethodOutput,
  WeightValueEntry,
  WeightValues
} from "@/types/dataset";
import { LayerType } from "@/types/map";
import { GridConfigResponse } from "@/types/project";
import {
  EOutputAnalysisType,
  EOutputCoverageType,
  EOutputGridType,
  type ScoringOptions,
  ScoringOptionsEnum,
  type SpatialConfig
} from "@/types/spatial";
import { CustomError, UnauthorizedStreamError } from "@/utils/errors";

import {
  AnalysisConfiguration,
  AnalysisDatasetFilter,
  OutputAnalysis,
  WorkspaceDatasetErrorCode
} from "@lokasi-ai-web-sdk/agent-ui";

import { getAggregationConfig } from "../project/utils";
import {
  AgentDatasetProposal,
  AgentWorkspaceDataset,
  PendingDataset,
  RowLimitError
} from "./types";

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * `fitMapToLayerBound` reads `boundary.features` unguarded, so an area the
 * backend returned without a shape would throw on the way to the camera.
 */
export const hasBoundary = (area: AdministrativeArea | null | undefined) =>
  (area?.boundary?.features?.length ?? 0) > 0;

/**
 * Normalises the agent's filters into this app's wire model.
 *
 * Both spellings are accepted: the SDK declares `filter_attribute_id` +
 * `filter: [{ value }]` but never parses the proposal at runtime, so the wire
 * shape is the backend's to choose, while this repo's own shape is
 * `{ id, filter }`.
 */
export const toFilterPayload = (
  agentFilters: readonly AnalysisDatasetFilter[] | undefined
): DatasetFilterPayload[] => {
  if (!agentFilters?.length) return [];

  return agentFilters.flatMap((agentFilter): DatasetFilterPayload[] => {
    if (!isRecord(agentFilter)) return [];

    const attributeId = agentFilter.filter_attribute_id ?? agentFilter.id ?? "";

    if (!attributeId) return [];

    const attributeCode =
      agentFilter.filter_attribute_column ?? agentFilter.attribute_code;

    // A list of `{ value }` objects collapses to the list of values; anything
    // else (a min/max or date envelope) is already in this app's shape.
    const filterValues = Array.isArray(agentFilter.filter)
      ? agentFilter.filter
          .map((entry) =>
            isRecord(entry) && typeof entry.value === "string"
              ? entry.value
              : undefined
          )
          .filter((value): value is string => value !== undefined)
      : agentFilter.filter;

    return [
      {
        id: attributeId,
        ...(typeof attributeCode === "string"
          ? { attribute_code: attributeCode }
          : {}),
        ...(filterValues !== undefined
          ? { filter: filterValues as DatasetFilterPayload["filter"] }
          : {})
      }
    ];
  });
};

export const toPendingDataset = (
  proposal: AgentDatasetProposal
): PendingDataset | null => {
  // The SDK types `dataset_id` as required but never parses the proposal, so a
  // malformed one arrives here with it missing.
  const datasetId = proposal.dataset_id?.trim();

  if (!datasetId) return null;

  return {
    id: datasetId,
    filters: toFilterPayload(proposal.filters),
    grid_config: getAggregationConfig(
      isRecord(proposal.grid_config)
        ? (proposal.grid_config as GridConfigResponse)
        : null
    )
  };
};

export const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : null;

/**
 * The SDK refuses a `failed` result that carries no message, and a refusal there
 * costs the backend's own reason with it. This is the floor for a cause whose
 * words are empty or missing entirely.
 */
const DATASET_FAILURE_FALLBACK = "The dataset could not be loaded.";

const failureMessage = (
  ...candidates: readonly (string | null | undefined)[]
): string =>
  candidates.find(
    (candidate) => typeof candidate === "string" && candidate.trim() !== ""
  ) ?? DATASET_FAILURE_FALLBACK;

/**
 * Maps a load failure onto the vocabulary the agent understands.
 *
 * Unrecognised backend reasons pass through verbatim — the SDK's code type is
 * an open string, so the backend's own reason beats reporting "unknown".
 */
export const toWorkspaceError = (
  error: unknown
): { code: WorkspaceDatasetErrorCode; message: string } => {
  if (error instanceof RowLimitError) {
    return {
      code: "ROW_LIMIT_EXCEEDED",
      message: failureMessage(error.message)
    };
  }

  if (error instanceof UnauthorizedStreamError) {
    return {
      code: "ERR_AUTH_UNAUTHORIZED",
      message: failureMessage(getErrorReasonMessage("ERR_AUTH_UNAUTHORIZED"))
    };
  }

  if (isRecord(error) && error.name === "AbortError") {
    return {
      code: "ERR_STREAM_CANCELED",
      message: failureMessage(getErrorReasonMessage("ERR_STREAM_CANCELED"))
    };
  }

  const reason = error instanceof CustomError ? error.reason : undefined;

  if (reason === "ERR_SUBSCRIPTION_NOT_FOUND") {
    return {
      code: "NOT_SUBSCRIBED",
      message: failureMessage(getErrorReasonMessage(reason))
    };
  }

  if (reason) {
    return {
      code: reason,
      message: failureMessage(
        getErrorReasonMessage(reason),
        getErrorMessage(error)
      )
    };
  }

  return {
    code: "UNKNOWN",
    message: failureMessage(getErrorMessage(error))
  };
};

/**
 * The scoring options the agent may pick.
 *
 * `LOG_Z_SCORE` is deliberately absent: advanced normalisation needs the
 * `log_mean`/`log_std_dev` only a user can supply, so selecting it here would
 * put the app in a mode that cannot be saved (see
 * `hasInvalidAdvancedNormalization`).
 */
const AGENT_SCORING_OPTIONS: ScoringOptions[] = [
  ScoringOptionsEnum.RAW,
  ScoringOptionsEnum.SCALED,
  ScoringOptionsEnum.NORMALIZED
];

/** Both resolution lists are keyed by the level as a string. */
const findResolution = <T extends { value: string }>(
  options: readonly T[],
  level: number | undefined
): T | null => options.find((option) => option.value === String(level)) ?? null;

/**
 * Reads the agent's output analysis into the app's spatial configuration.
 *
 * The mirror of the saved-configuration loader (`mapSpatialConfig`), kept
 * separate because the two vocabularies are not the same: the agent sends
 * `output_mode: "STANDARD_GRID"` where this app's enum is `STANDARD`, and
 * `proportion_method: "AREA"` where it means area-based (`dasimetry: false`).
 *
 * Returns only the keys it could read — a `type` this app has no output for
 * leaves the current one alone rather than guessing (contract rule 4).
 */
export const toSpatialConfigPatch = (
  output: OutputAnalysis | null | undefined,
  isCoverageTypeEnabled: boolean
): Partial<SpatialConfig> | null => {
  if (!output) return null;

  const patch: Partial<SpatialConfig> = {};
  const type = output.type?.toLowerCase();

  if (type === EOutputAnalysisType.PROFILING) {
    patch.outputType = EOutputAnalysisType.PROFILING;
    patch.gridType = null;
    patch.gridResolution = null;
    patch.h3Resolution = null;
  } else if (type === EOutputGridType.GEOHASH || type === EOutputGridType.H3) {
    const isGeohash = type === EOutputGridType.GEOHASH;

    patch.outputType = EOutputAnalysisType.GRID;
    patch.gridType = isGeohash ? EOutputGridType.GEOHASH : EOutputGridType.H3;
    patch.gridResolution = isGeohash
      ? findResolution(GEOHASH_RESOLUTION, output.grid_level)
      : null;
    patch.h3Resolution = isGeohash
      ? null
      : findResolution(H3_RESOLUTION, output.grid_level);

    if (
      output.grid_level !== undefined &&
      !patch.gridResolution &&
      !patch.h3Resolution
    ) {
      console.warn("Agent output analysis has no resolution this app offers", {
        output
      });
    }
  }

  const scoringOption = AGENT_SCORING_OPTIONS.find(
    (option) => option === output.scoring_option?.toUpperCase()
  );

  if (scoringOption) patch.scoringOption = scoringOption;

  // Absent means "not stated", which is not the same as area-based: leave
  // whatever is configured rather than asserting a method the agent never sent.
  if (output.proportion_method) {
    patch.dasimetry =
      output.proportion_method === ProportionMethodOutput.DASYMETRIC;
  }

  const catchment = isRecord(output.custom_catchment)
    ? output.custom_catchment
    : undefined;

  // Coverage travels as a pair, and only when the agent stated it: a block
  // without either key says nothing about coverage, and defaulting it to
  // STANDARD would quietly drop a custom catchment the user configured.
  if (isCoverageTypeEnabled && (output.output_mode || catchment)) {
    const radius = catchment?.radius_m;
    const hasRadius = typeof radius === "number" && Number.isFinite(radius);

    patch.outputMode =
      hasRadius || /custom/i.test(output.output_mode ?? "")
        ? EOutputCoverageType.CUSTOM_CATCHMENT
        : EOutputCoverageType.STANDARD;
    patch.customCatchmentRadius = hasRadius ? radius : null;
  }

  return Object.keys(patch).length > 0 ? patch : null;
};

export type AgentWeightPatch = {
  datasetId: string;
  data: WeightValues[string];
};

const isSetWeight = (weight: unknown): weight is number =>
  typeof weight === "number" && Number.isFinite(weight);

/**
 * Reads the agent's dataset weights into the app's weight model.
 *
 * Driven by the adapter's ownership ledger rather than by the workspace's
 * dataset map, so a dataset the user added by hand is never re-weighted and a
 * `weightValues` key for a layer that is not on the map cannot be created.
 *
 * A patch is produced only for an entry the agent actually said something about
 * — the first snapshot of an approved dataset carries `weight: null,
 * include_scoring: true, attributes: []`, which is the absence of a weighting
 * decision and must not overwrite the app's own normalised share.
 */
export const toWeightPatches = (
  desired: AnalysisConfiguration["datasets"] | undefined,
  ledger: ReadonlyMap<string, { compositeId: string }>,
  datasets: DatasetsMap,
  weightValues: WeightValues
): AgentWeightPatch[] => {
  const patches: AgentWeightPatch[] = [];

  for (const [key, owned] of ledger) {
    const entry = desired?.[key] as AgentWorkspaceDataset | undefined;
    const dataset = datasets[owned.compositeId];

    // A layer that is no longer in the store has nothing to weight — a stream
    // that failed, or a removal already on its way through.
    if (!entry || !dataset) continue;

    const current = weightValues[owned.compositeId];
    const named = (entry.attributes ?? []).filter(
      (attribute) =>
        attribute?.attribute_id &&
        (isSetWeight(attribute.weight_score) ||
          typeof attribute.include_scoring === "boolean")
    );

    const hasWeight = isSetWeight(entry.weight);
    const saidSomething =
      hasWeight ||
      entry.include_scoring === false ||
      named.length > 0 ||
      // The agent putting a dataset back into scoring is a decision too, and
      // the only record of the old one is the store.
      current?.include_scoring === false;

    if (!saidSomething) continue;

    // Started from what the store holds: `initializeWeightValues` seeded an
    // entry per schema attribute, and only the ids the agent named are patched.
    // The absolute values do not have to add up to the dataset weight — each
    // attribute's percentage is its share *within* the dataset.
    const attributes: Record<string, WeightValueEntry> = {
      ...(current?.attributes ?? {})
    };
    const isPoi = dataset.properties.spatialType === LayerType.POINT;

    for (const attribute of named) {
      const attributeId = attribute.attribute_id as string;
      const seeded = attributes[attributeId];

      // The store keys attributes by `paired_key_attribute_id || id` while the
      // agent sends the plain schema id. An id that matches nothing would never
      // reach the analysis payload (that one walks the schema) but would still
      // dilute the real attributes' percentages, so it is dropped.
      if (!seeded) {
        console.warn("Agent weighted an attribute this dataset does not have", {
          attributeId,
          datasetId: owned.compositeId
        });
        continue;
      }

      const includeScoring = attribute.include_scoring;

      attributes[attributeId] = {
        ...seeded,
        ...(isSetWeight(attribute.weight_score)
          ? { weight: String(attribute.weight_score) }
          : {}),
        // For a POI dataset only the primary attribute scores, and both the
        // reducer and the payload builder derive that themselves — writing it
        // here would only disagree with them.
        ...(!isPoi && typeof includeScoring === "boolean"
          ? { include_scoring: includeScoring }
          : {})
      };
    }

    patches.push({
      datasetId: owned.compositeId,
      data: {
        ...current,
        weight: hasWeight ? String(entry.weight) : (current?.weight ?? ""),
        include_scoring: entry.include_scoring !== false,
        attributes
      }
    });
  }

  return patches;
};
